import { getCalApi } from '@calcom/embed-react';
import {
  ExpandMore as ExpandMoreIcon,
  ScheduleOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { Box, Button, Chip, Collapse, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ensureAdvisorReviewFixtures,
  filterTasksForProperty,
  formatReviewTaskTitle,
  getHOATaskState,
  getSellerDisclosureTaskState,
  hasRequiredOnboardingAnswer,
  hasHOADocuments,
  HOA_REVIEW_TASK_TITLE,
  KICKOFF_TASK_TITLE,
  LOCAL_TASK_FIXTURE_PREFIX,
  MLS_DOCUMENT_CHECK_TASK_TITLE,
  PRE_APPROVAL_TASK_TITLE,
  SELLER_DISCLOSURES_REVIEW_TASK_TITLE,
} from './taskVisibility';

import { CardMode } from '@/context/DashboardLayoutProvider';
import { useTasks } from '@/hooks/tasks';
import {
  CLIENT_GROUPS_QUERY_KEY,
  useClientGroups,
  useUpdateDeal,
  useUpdateListing,
} from '@/hooks/useClientGroups';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useHOAChecklist } from '@/hooks/useHOAChecklist';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { useUserRole } from '@/hooks/useUserRole';
import { Task, TaskGroup, TaskRole, groupTasks } from '@/models/Task';
import { certifyListing } from '@/services/api/clientGroups';
import { getPublicUserProfile } from '@/services/api/users/api';
import { DocumentReadiness } from '@/types/clientGroups';

/**
 * Normalizes an advisor's stored calLink (full URL, protocol-less URL, or
 * bare path) into the `user/event` slug that Cal.com's embed expects.
 */
const calLinkPathFrom = (calLink: string | undefined | null): string | null => {
  if (!calLink) return null;
  let trimmed = calLink.trim();
  if (!trimmed) return null;
  trimmed = trimmed.replace(/^https?:\/\//i, '');
  const calComIdx = trimmed.toLowerCase().indexOf('cal.com/');
  if (calComIdx !== -1) {
    trimmed = trimmed.slice(calComIdx + 'cal.com/'.length);
  }
  const path = trimmed.replace(/^\/+/, '').replace(/\/+$/, '');
  return path || null;
};

/**
 * Task titles/subtitles come from the backend and still reference the old
 * ConditionScore/ContextScore/Insights nomenclature. Display-only relabel,
 * exact-match on observed strings rather than substring regex — task prose
 * doesn't read naturally under a mechanical find/replace, so these only
 * touch known strings rather than rewriting arbitrary copy.
 */
const TASK_TITLE_RELABELS: Record<string, string> = {
  'Review ConditionScore': 'Review The Home',
  'Review ContextScore': 'Review The Area',
  'Review insights': 'Review Repair Planner',
  'Schedule kick-off call': 'Schedule: Kick-off Call',
  'Schedule due diligence review': 'Schedule: Due diligence review',
};
const INITIAL_REVIEW_TITLE_RELABELS: Record<string, string> = {
  'Prepare due diligence': 'Prepare initial property review',
  'Dashboard ready': 'Initial dashboard ready',
  'Schedule due diligence review': 'Schedule: Initial property review',
};
const relabelTaskTitle = (title: string, isDocumentReviewActive: boolean): string =>
  formatReviewTaskTitle(
    (!isDocumentReviewActive ? INITIAL_REVIEW_TITLE_RELABELS[title] : undefined) ??
      TASK_TITLE_RELABELS[title] ??
      title,
  );

const TASK_SUBTITLE_RELABELS: Record<string, string> = {
  'Advisor runs ConditionScore and uploads any available disclosure docs':
    'Advisor processes reports and uploads any available disclosure docs',
  'Explore location quality, environmental risks, investment signals, and neighborhood context':
    'Review location access, amenities, parcel characteristics, natural hazards, health, and safety',
  'Explore location quality, environmental risks, safety, and neighborhood context':
    'Review location access, amenities, parcel characteristics, natural hazards, health, and safety',
};
const INITIAL_REVIEW_SUBTITLE_RELABELS: Record<string, string> = {
  'Advisor runs ConditionScore and uploads any available disclosure docs':
    'Advisor prepares the available property, area, and market data',
  'Client gets full access to scores, flags, and documents before the meeting':
    'Client gets access to the initial Area and Market Insights analysis',
  '60-min screen-share to review disclosures, inspection reports, and insights':
    'Review available property intelligence before deciding how to proceed',
};
const relabelTaskSubtitle = (subtitle: string, isDocumentReviewActive: boolean): string =>
  (!isDocumentReviewActive ? INITIAL_REVIEW_SUBTITLE_RELABELS[subtitle] : undefined) ??
  TASK_SUBTITLE_RELABELS[subtitle] ??
  subtitle;

const TIMELINE_DOTTED_COLOR = '#333333';
const LIME_ACCENT = '#C0FF02';
const TASK_ROW_MIN_HEIGHT = 72; // uniform row height so dotted connectors match
interface RoleChipProps {
  role: TaskRole;
}

const SingleChip = ({ label, isAdvisor }: { label: string; isAdvisor: boolean }) => (
  <Chip
    label={label}
    sx={{
      flexShrink: 0,
      fontSize: 10,
      fontWeight: isAdvisor ? 600 : 500,
      letterSpacing: '0.02em',
      borderRadius: '6px',
      height: 'auto',
      py: '3px',
      px: '8px',
      backgroundColor: 'var(--tx-fill-2)',
      color: 'var(--tx-muted)',
      border: 0,
      '& .MuiChip-label': { px: 0 },
    }}
  />
);

const RoleChip = ({ role }: RoleChipProps) => {
  if (role === TaskRole.Both) {
    return (
      <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
        <SingleChip label="Advisor" isAdvisor />
        <SingleChip label="Client" isAdvisor={false} />
      </Box>
    );
  }
  return (
    <SingleChip
      label={role === TaskRole.Advisor ? 'Advisor' : 'Client'}
      isAdvisor={role === TaskRole.Advisor}
    />
  );
};

interface TaskItemProps {
  task: Task;
  isLast: boolean;
  isCompleted: boolean;
  completedAt?: number;
  advisorCalLinkPath: string | null;
  onToggle: (task: Task) => void;
  cardMode: CardMode;
  canComplete: boolean;
  actionContent?: React.ReactNode;
  isDocumentReviewActive: boolean;
  isWaiting?: boolean;
  disabledReason?: string;
}

const TaskItem = ({
  task,
  isLast,
  isCompleted,
  completedAt,
  advisorCalLinkPath,
  onToggle,
  cardMode,
  canComplete,
  actionContent,
  isDocumentReviewActive,
  isWaiting = false,
  disabledReason,
}: TaskItemProps) => {
  const isDark = cardMode === 'dark';
  const titleColor = isDark ? '#FFFFFF' : '#0A0A0A';
  const subtitleColor = isDark ? '#B0B0B0' : '#666666';
  const circleColor = isDark ? '#404040E6' : '#FFFFFF90';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '22px',
        position: 'relative',
        alignItems: actionContent ? 'flex-start' : 'center',
        minHeight: TASK_ROW_MIN_HEIGHT,
      }}>
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: 11,
            top: actionContent ? 36 : '50%',
            height: actionContent ? 'calc(100% - 12px)' : 'calc(50% + 24px)',
            width: 0,
            borderLeft: `2px dotted ${TIMELINE_DOTTED_COLOR}`,
          }}
        />
      )}

      <Box
        onClick={canComplete && !isWaiting ? () => onToggle(task) : undefined}
        title={
          isWaiting
            ? 'Waiting for documents'
            : canComplete
            ? undefined
            : disabledReason ?? `Only the ${task.role} can complete this task`
        }
        sx={{
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: canComplete && !isWaiting ? 'pointer' : 'default',
          opacity: canComplete || isWaiting ? 1 : 0.5,
          zIndex: 1,
          backgroundColor: 'background.default',
          borderRadius: '50%',
          mt: actionContent ? '24px' : 0,
        }}>
        {isWaiting ? (
          <ScheduleOutlined sx={{ width: 18, height: 18, color: 'var(--tx-faint)' }} />
        ) : isCompleted ? (
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="11" fill={LIME_ACCENT} />
            <polyline
              points="6.5,11 9.5,14 15.5,8"
              stroke="#262626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        ) : (
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: circleColor,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          py: '12px',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          ...(!isLast ? { borderBottom: '1px solid rgba(255,255,255,0.07)' } : {}),
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 0.5,
          }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: 13,
              color: titleColor,
              flex: 1,
              minWidth: 0,
              textDecoration: isCompleted ? 'line-through' : 'none',
              opacity: isCompleted ? 0.6 : 1,
            }}>
            {relabelTaskTitle(task.title, isDocumentReviewActive)}
          </Typography>
        </Box>

        {task.subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: subtitleColor,
              display: 'block',
              fontSize: 11,
              lineHeight: 1.4,
              mb: 0.75,
              opacity: isCompleted ? 0.6 : 1,
            }}>
            {relabelTaskSubtitle(task.subtitle, isDocumentReviewActive)}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <RoleChip role={task.role} />
          {task.bookingNamespace && advisorCalLinkPath && !isCompleted && !isWaiting && (
            <Box
              component="button"
              type="button"
              data-cal-namespace={task.bookingNamespace}
              data-cal-link={advisorCalLinkPath}
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--tx-text)',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                '&:hover': { textDecoration: 'underline' },
              }}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 2 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Book meeting
            </Box>
          )}
          {isCompleted && completedAt && (
            <Typography
              sx={{
                ml: 'auto',
                fontSize: 10,
                color: 'var(--tx-faint)',
                whiteSpace: 'nowrap',
              }}>
              {format(new Date(completedAt * 1000), 'MMM d, h:mm a')}
            </Typography>
          )}
        </Box>
        {actionContent}
      </Box>
    </Box>
  );
};

interface FinalCertifyCardProps {
  allComplete: boolean;
  isCertified: boolean;
  certifiedInitials: string | null;
  certifiedByName: string | null;
  certifiedAt: number | null;
  isCertifying: boolean;
  errorMessage: string | null;
  /**
   * Whether the current user is allowed to submit the certification.
   * Only the buyer can certify; advisors and admins see a read-only state.
   */
  canCertify: boolean;
  onConfirm: (initials: string) => void;
}

const FinalCertifyCard = ({
  allComplete,
  isCertified,
  certifiedInitials,
  certifiedByName,
  certifiedAt,
  isCertifying,
  errorMessage,
  canCertify,
  onConfirm,
}: FinalCertifyCardProps) => {
  const [initials, setInitials] = useState('');
  const trimmed = initials.trim();
  const canConfirm = allComplete && trimmed.length > 0 && !isCertifying;

  if (isCertified && certifiedInitials) {
    return (
      <Box
        sx={{
          mx: 2,
          mt: 1.5,
          mb: 2,
          p: 1.5,
          backgroundColor: 'rgba(192,255,2,0.06)',
          border: 0,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
        <Box sx={{ px: 0.75, display: 'flex', alignItems: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="11" fill={LIME_ACCENT} />
            <polyline
              points="6.5,11 9.5,14 15.5,8"
              stroke="#262626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 12, color: 'var(--tx-text)' }}>
            Certified by{' '}
            <Box component="span" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
              {certifiedByName ?? certifiedInitials.toUpperCase()}
            </Box>
          </Typography>
          {certifiedAt && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
              {format(new Date(certifiedAt * 1000), 'MMM d, yyyy h:mm a')}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (!canCertify) {
    // Advisors and admins can see the deal's certify state but cannot sign for
    // the buyer. Render a quiet placeholder so it's clear the deal is awaiting
    // the buyer's initials, without exposing the input.
    return (
      <Box
        sx={{
          mx: 2,
          mt: '20px',
          mb: 2,
          p: '15px',
          backgroundColor: 'var(--tx-fill)',
          border: 0,
          borderRadius: '10px',
        }}>
        <Typography
          sx={{
            fontSize: 12,
            color: 'var(--tx-muted)',
            lineHeight: 1.45,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}>
          {!allComplete && <WarningAmberOutlined sx={{ fontSize: 14, color: '#EBC064' }} />}
          {allComplete
            ? 'Awaiting buyer certification.'
            : 'Buyer will certify once all tasks are complete.'}
        </Typography>
      </Box>
    );
  }

  const bodyText = allComplete
    ? "By initialing, I confirm my Hauser Advisor walked me through this property's " +
      'scores, inspection findings, environmental risks, and offer strategy — ' +
      "and that I'm moving forward with a clear understanding of what we found."
    : 'Complete all tasks above before certifying.';

  return (
    <Box
      sx={{
        mx: 2,
        mt: '20px',
        mb: 2,
        p: '15px',
        backgroundColor: 'var(--tx-fill)',
        border: 0,
        borderRadius: '10px',
      }}>
      <Typography
        sx={{
          fontSize: 12,
          color: 'var(--tx-muted)',
          mb: '15px',
          lineHeight: 1.45,
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
        }}>
        {!allComplete && <WarningAmberOutlined sx={{ fontSize: 14, color: '#EBC064' }} />}
        {bodyText}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          placeholder="Client initials"
          size="small"
          value={initials}
          onChange={(e) => setInitials(e.target.value)}
          disabled={isCertifying}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: 12,
              backgroundColor: 'var(--tx-fill)',
              borderRadius: '8px',
              '& fieldset': { border: 0 },
              '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
              '&.Mui-focused fieldset': { border: 0 },
            },
            '& .MuiOutlinedInput-input': { py: '6px', color: 'var(--tx-text)' },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'var(--tx-faint)',
              opacity: 1,
            },
          }}
        />
        <Button
          variant="contained"
          size="small"
          disabled={!canConfirm}
          onClick={() => onConfirm(trimmed)}
          sx={{
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: '8px',
            backgroundColor: canConfirm ? LIME_ACCENT : 'var(--tx-fill-2)',
            color: canConfirm ? 'var(--tx-text)' : 'var(--tx-text)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: canConfirm ? '#AEE800' : 'var(--tx-fill-3)',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: 'var(--tx-fill)',
              color: 'var(--tx-faint)',
            },
          }}>
          {isCertifying ? 'Confirming…' : 'Confirm'}
        </Button>
      </Box>
      {errorMessage && (
        <Typography sx={{ mt: 1, fontSize: 11, color: '#E57373' }}>{errorMessage}</Typography>
      )}
    </Box>
  );
};

interface GroupSectionProps {
  groupLabel: string;
  completedCount: number;
  totalCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  cardMode: CardMode;
  children: React.ReactNode;
}

const GroupSection = ({
  groupLabel,
  completedCount,
  totalCount,
  isExpanded,
  onToggleExpand,
  cardMode,
  children,
}: GroupSectionProps) => {
  const isDark = cardMode === 'dark';
  const headerColor = isDark ? '#BBB7AF' : '#262626';
  const countColor = 'var(--tx-faint)';
  const isGroupComplete = completedCount === totalCount;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={onToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: headerColor,
            }}>
            {groupLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: isGroupComplete ? LIME_ACCENT : countColor,
            }}>
            {completedCount}/{totalCount}
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: headerColor,
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
      </Box>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const WaitingTaskSection = ({
  groupLabel,
  cardMode,
  children,
}: {
  groupLabel: string;
  cardMode: CardMode;
  children: React.ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const headerColor = cardMode === 'dark' ? '#8B8680' : '#666666';

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setIsExpanded((expanded) => !expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
        }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: headerColor,
          }}>
          {groupLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box
            sx={{
              // a quiet square tag (the app's Tag), not an outlined pill
              px: '6px',
              py: '3px',
              borderRadius: '6px',
              border: '1px solid transparent',
              backgroundColor: 'rgba(var(--tx-ink-rgb), 0.06)',
              flexShrink: 0,
            }}>
            <Typography
              sx={{
                color: headerColor,
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}>
              Waiting
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: headerColor,
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
      </Box>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

interface ProgressBarProps {
  completed: number;
  total: number;
}

const ProgressBar = ({ completed, total }: ProgressBarProps) => {
  const pct = total === 0 ? 0 : (completed / total) * 100;
  return (
    <Box sx={{ px: 2, pt: 0.5, pb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
        <Typography sx={{ fontSize: 11, color: 'var(--tx-faint)' }}>
          {completed} of {total} complete
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 2,
          backgroundColor: 'var(--tx-fill-2)',
          borderRadius: 1,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: LIME_ACCENT,
            transition: 'width 0.25s ease',
          }}
        />
      </Box>
    </Box>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <Box sx={{ px: 2, py: 6, textAlign: 'center' }}>
    <Typography sx={{ fontSize: 12, color: 'var(--tx-faint)' }}>{message}</Typography>
  </Box>
);

interface DocumentReadinessControlProps {
  value: DocumentReadiness | undefined;
  canEdit: boolean;
  isSaving: boolean;
  errorMessage?: string;
  onChange: (value: DocumentReadiness) => void;
}

const DocumentReadinessControl = ({
  value,
  canEdit,
  isSaving,
  errorMessage,
  onChange,
}: DocumentReadinessControlProps) => {
  const effectiveValue = value;

  return (
    <Box
      sx={{
        mb: 1.5,
        mt: '15px',
        px: '10px',
        py: '8px',
        borderRadius: '8px',
        border: '1px solid transparent',
        backgroundColor: 'var(--tx-fill)',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
        }}>
        <Typography sx={{ color: 'var(--tx-text)', fontSize: 12, fontWeight: 600 }}>
          Listing documents
          <Box component="span" sx={{ color: '#DB5282' }}>
            {' *'}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {(
            [
              ['available', 'Yes'],
              ['not_available_yet', 'No'],
            ] as const
          ).map(([option, label]) => {
            const selected = effectiveValue === option;
            const disabled = !canEdit || isSaving;
            return (
              <Button
                key={option}
                size="small"
                disabled={disabled}
                onClick={() => onChange(option)}
                sx={{
                  minWidth: 0,
                  px: '10px',
                  py: '3px',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: 11,
                  lineHeight: 1.3,
                  // the app's segment: the chosen answer a raised tonal thumb, the other plain muted text; no outlines
                  fontWeight: selected ? 600 : 500,
                  border: '1px solid transparent',
                  background: selected ? 'var(--tx-sel-bg)' : 'transparent',
                  boxShadow: selected ? 'var(--tx-sel-shadow)' : 'none',
                  color: selected ? 'var(--tx-text)' : 'var(--tx-muted)',
                  '&:hover': {
                    background: selected ? 'var(--tx-sel-bg)' : 'var(--tx-fill)',
                    color: 'var(--tx-text)',
                  },
                  '&.Mui-disabled': {
                    color: selected ? 'var(--tx-text)' : 'var(--tx-faint)',
                    background: selected ? 'var(--tx-sel-bg)' : 'transparent',
                    boxShadow: selected ? 'var(--tx-sel-shadow)' : 'none',
                    opacity: 1,
                  },
                }}>
                {label}
              </Button>
            );
          })}
        </Box>
      </Box>
      {errorMessage && (
        <Typography sx={{ color: '#E57373', fontSize: 11, mt: 0.75 }}>{errorMessage}</Typography>
      )}
    </Box>
  );
};

const PreApprovalControl = ({
  value,
  canEdit,
  isSaving,
  errorMessage,
  onChange,
}: {
  value: boolean | undefined;
  canEdit: boolean;
  isSaving: boolean;
  errorMessage?: string;
  onChange: (value: boolean) => void;
}) => (
  <Box
    sx={{
      mb: 1.5,
      mt: '15px',
      px: '10px',
      py: '8px',
      borderRadius: '8px',
      border: '1px solid transparent',
      backgroundColor: 'var(--tx-fill)',
    }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
      }}>
      <Typography sx={{ color: 'var(--tx-text)', fontSize: 12, fontWeight: 600 }}>
        Pre-approval letter
        <Box component="span" sx={{ color: '#DB5282' }}>
          {' *'}
        </Box>
      </Typography>
      <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        {(
          [
            [true, 'Yes'],
            [false, 'No'],
          ] as const
        ).map(([option, label]) => {
          const selected = value === option;
          return (
            <Button
              key={label}
              size="small"
              disabled={!canEdit || isSaving}
              onClick={() => onChange(option)}
              sx={{
                minWidth: 0,
                px: '10px',
                py: '3px',
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 11,
                lineHeight: 1.3,
                // the app's segment: the chosen answer a raised tonal thumb, the other plain muted text; no outlines
                fontWeight: selected ? 600 : 500,
                border: '1px solid transparent',
                background: selected ? 'var(--tx-sel-bg)' : 'transparent',
                boxShadow: selected ? 'var(--tx-sel-shadow)' : 'none',
                color: selected ? 'var(--tx-text)' : 'var(--tx-muted)',
                '&:hover': {
                  background: selected ? 'var(--tx-sel-bg)' : 'var(--tx-fill)',
                  color: 'var(--tx-text)',
                },
                '&.Mui-disabled': {
                  color: selected ? 'var(--tx-text)' : 'var(--tx-faint)',
                  background: selected ? 'var(--tx-sel-bg)' : 'transparent',
                  boxShadow: selected ? 'var(--tx-sel-shadow)' : 'none',
                  opacity: 1,
                },
              }}>
              {label}
            </Button>
          );
        })}
      </Box>
    </Box>
    {errorMessage && (
      <Typography sx={{ color: '#E57373', fontSize: 11, mt: 0.75 }}>{errorMessage}</Typography>
    )}
  </Box>
);

const DocumentTaskCallout = ({
  label,
  state,
  isDocumentReviewActive,
}: {
  label: 'Seller disclosures' | 'HOA documents';
  state: 'missing' | 'processing' | 'attention';
  isDocumentReviewActive: boolean;
}) => {
  const message =
    state === 'missing'
      ? `${label} have not been uploaded yet.`
      : state === 'processing'
      ? `${label} are uploaded and processing.`
      : `${label} need attention before review.`;
  const impact = isDocumentReviewActive ? '' : ' This does not block the initial property review.';

  return (
    <Box
      sx={{
        mt: '10px',
        mb: '4px',
        px: '10px',
        py: '8px',
        borderRadius: '8px',
        border: 0,
        backgroundColor:
          state === 'attention' ? 'rgba(229,115,115,0.06)' : 'rgba(255,255,255,0.03)',
      }}>
      <Typography
        sx={{
          color: state === 'attention' ? '#E57373' : 'var(--tx-faint)',
          fontSize: 11,
          lineHeight: 1.4,
        }}>
        {message}
        {impact}
      </Typography>
    </Box>
  );
};

interface TasksPanelProps {
  cardMode: CardMode;
  /** Deal whose tasks should render. When absent, the empty state is shown. */
  dealId?: string;
  /** Active listing — when provided, fetches Onboarding + property-level DDR/Offer Strategy tasks. */
  listingId?: string;
  /** Optional header rendered above the task list when dealId is present. */
  header?: React.ReactNode;
  /** Message to show when dealId is absent. Defaults to a generic prompt. */
  emptyMessage?: string;
}

export const TasksPanel = ({
  cardMode,
  dealId,
  listingId,
  header,
  emptyMessage,
}: TasksPanelProps) => {
  const queryClient = useQueryClient();
  const [manualExpandedGroup, setManualExpandedGroup] = useState<TaskGroup | null | undefined>(
    undefined,
  );
  const [certifyError, setCertifyError] = useState<string | null>(null);
  const [localDocumentReadiness, setLocalDocumentReadiness] = useState<
    DocumentReadiness | undefined
  >();
  const [localCompletedTaskIds, setLocalCompletedTaskIds] = useState<Set<string>>(() => new Set());
  const [localPreApprovalLetterAvailable, setLocalPreApprovalLetterAvailable] = useState<
    boolean | undefined
  >();

  const { isAdvisor, isAdmin, isClient } = useUserRole();

  // Only the buyer can submit certification; backend enforces group-membership.
  const canCertify = isClient && !isAdvisor && !isAdmin;
  const { userProfile } = useUserProfile();
  const { groups: clientGroups } = useClientGroups();
  const activeDeal = useMemo(
    () => clientGroups.flatMap((group) => group.deals).find((deal) => deal.id === dealId),
    [clientGroups, dealId],
  );
  const buyerAdvisorId = useMemo(() => {
    if (isAdvisor || !dealId) return undefined;
    const group = clientGroups.find((g) => g.deals?.some((d) => d.id === dealId));
    return group?.advisorId;
  }, [isAdvisor, dealId, clientGroups]);
  const { data: advisorPublicProfile } = useQuery({
    queryKey: ['public-profile', buyerAdvisorId],
    queryFn: () => getPublicUserProfile(buyerAdvisorId as string),
    enabled: Boolean(buyerAdvisorId),
    staleTime: 5 * 60 * 1000,
  });
  const rawAdvisorCalLink = isAdvisor ? userProfile?.calLink : advisorPublicProfile?.calLink;
  const advisorCalLinkPath = useMemo(() => calLinkPathFrom(rawAdvisorCalLink), [rawAdvisorCalLink]);

  const activeListing = useMemo(() => {
    if (!listingId) return undefined;
    for (const g of clientGroups) {
      for (const d of g.deals) {
        const l = d.listings?.find((ll) => ll.id === listingId);
        if (l) return l;
      }
    }
    return undefined;
  }, [clientGroups, listingId]);

  const { data: hoaChecklist } = useHOAChecklist(activeListing?.propertyId);
  const { documentRecords } = useDocumentRecords(activeListing?.propertyId);
  const sellerDisclosureTaskState = getSellerDisclosureTaskState(documentRecords);
  const isSellerDisclosuresActive = sellerDisclosureTaskState !== 'missing';
  const hoaChecklistHasReceivedDocument =
    hoaChecklist?.hoaChecklist.some((item) => item.status === 'RECEIVED') ?? false;
  const hoaTaskState = getHOATaskState(documentRecords, hoaChecklistHasReceivedDocument);
  const isHOARelevant = hoaChecklist?.hasHOA === true || hasHOADocuments(documentRecords);
  const effectiveDocumentReadiness = activeListing?.documentReadiness ?? localDocumentReadiness;
  const isDocumentReviewActive = effectiveDocumentReadiness === 'available';
  const isHOAActive = isDocumentReviewActive ? isHOARelevant : hoaTaskState !== 'missing';
  const effectivePreApprovalLetterAvailable =
    activeDeal?.preApprovalLetterAvailable ?? localPreApprovalLetterAvailable;
  const updateListing = useUpdateListing();
  const updateDeal = useUpdateDeal();

  const {
    tasks: allTasks,
    completedIds,
    isLoadingTasks,
    toggleTaskComplete,
  } = useTasks(dealId, listingId);

  const tasksWithFixtures = useMemo(
    () => (dealId ? ensureAdvisorReviewFixtures(allTasks, dealId, listingId) : allTasks),
    [allTasks, dealId, listingId],
  );
  const mlsDocumentCheckTask = tasksWithFixtures.find(
    (task) => task.title === MLS_DOCUMENT_CHECK_TASK_TITLE,
  );
  const handleDocumentReadinessChange = (documentReadiness: DocumentReadiness) => {
    if (!listingId || updateListing.isPending) return;
    if (mlsDocumentCheckTask?.id.startsWith(LOCAL_TASK_FIXTURE_PREFIX)) {
      setLocalDocumentReadiness(documentReadiness);
      return;
    }
    updateListing.mutate({ listingId, data: { documentReadiness } });
  };
  const handlePreApprovalChange = (preApprovalLetterAvailable: boolean) => {
    if (!dealId || updateDeal.isPending) return;
    if (mlsDocumentCheckTask?.id.startsWith(LOCAL_TASK_FIXTURE_PREFIX)) {
      setLocalPreApprovalLetterAvailable(preApprovalLetterAvailable);
      return;
    }
    updateDeal.mutate({ dealId, data: { preApprovalLetterAvailable } });
  };
  const effectiveCompletedIds = useMemo(() => {
    const ids = new Set(completedIds);
    localCompletedTaskIds.forEach((taskId) => ids.add(taskId));
    return ids;
  }, [completedIds, localCompletedTaskIds]);
  const tasks = useMemo(
    () =>
      filterTasksForProperty(
        tasksWithFixtures,
        isHOAActive,
        isSellerDisclosuresActive,
        isDocumentReviewActive,
      ).filter(
        (task) =>
          task.title !== PRE_APPROVAL_TASK_TITLE || effectivePreApprovalLetterAvailable === false,
      ),
    [
      tasksWithFixtures,
      isHOAActive,
      isSellerDisclosuresActive,
      isDocumentReviewActive,
      effectivePreApprovalLetterAvailable,
    ],
  );
  const taskGroups = useMemo(() => groupTasks(tasks), [tasks]);
  const futureDueDiligenceTasks = useMemo(() => {
    if (effectiveDocumentReadiness !== 'not_available_yet') return [];
    const currentTaskIds = new Set(tasks.map((task) => task.id));
    return filterTasksForProperty(tasksWithFixtures, isHOARelevant, false, true).filter(
      (task) => task.group === TaskGroup.DueDiligenceReview && !currentTaskIds.has(task.id),
    );
  }, [effectiveDocumentReadiness, tasks, tasksWithFixtures, isHOARelevant]);
  const futureContingencyReviewTasks = useMemo(() => {
    if (effectiveDocumentReadiness !== 'not_available_yet') return [];
    return filterTasksForProperty(tasksWithFixtures, isHOARelevant, false, true).filter(
      (task) => task.group === TaskGroup.ContingencyReview,
    );
  }, [effectiveDocumentReadiness, tasksWithFixtures, isHOARelevant]);
  const waitingTaskIds = useMemo(
    () =>
      new Set(
        tasks
          .filter(
            (task) =>
              (task.title === SELLER_DISCLOSURES_REVIEW_TASK_TITLE &&
                sellerDisclosureTaskState !== 'ready') ||
              (task.title === HOA_REVIEW_TASK_TITLE && hoaTaskState !== 'ready'),
          )
          .map((task) => task.id),
      ),
    [tasks, sellerDisclosureTaskState, hoaTaskState],
  );
  const actionableTasks = useMemo(
    () => tasks.filter((task) => !waitingTaskIds.has(task.id)),
    [tasks, waitingTaskIds],
  );
  const isTaskSatisfied = useCallback(
    (task: Task): boolean =>
      effectiveCompletedIds.has(task.id) &&
      hasRequiredOnboardingAnswer(
        task.title,
        effectiveDocumentReadiness,
        effectivePreApprovalLetterAvailable,
      ),
    [effectiveCompletedIds, effectiveDocumentReadiness, effectivePreApprovalLetterAvailable],
  );
  const completedCount = useMemo(
    () => actionableTasks.filter(isTaskSatisfied).length,
    [actionableTasks, isTaskSatisfied],
  );
  const totalCount = actionableTasks.length;
  const allComplete =
    totalCount > 0 &&
    completedCount === totalCount &&
    (!isDocumentReviewActive || waitingTaskIds.size === 0);

  const certifiedByName = useMemo(() => {
    if (!activeListing?.certifiedBy) return null;
    for (const g of clientGroups) {
      const member = g.members.find((m) => m.userId === activeListing.certifiedBy);
      if (member) return member.name;
    }
    return null;
  }, [clientGroups, activeListing?.certifiedBy]);

  const { mutate: certify, isPending: isCertifying } = useMutation({
    mutationFn: ({ initials }: { initials: string }) =>
      certifyListing(listingId as string, initials),
    onSuccess: () => {
      setCertifyError(null);
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to certify';
      setCertifyError(message);
    },
  });

  const handleToggle = (task: Task) => {
    if (task.id.startsWith(LOCAL_TASK_FIXTURE_PREFIX)) {
      setLocalCompletedTaskIds((current) => {
        const next = new Set(current);
        if (next.has(task.id)) next.delete(task.id);
        else next.add(task.id);
        return next;
      });
      return;
    }
    toggleTaskComplete({ taskId: task.id, isCompleted: Boolean(task.completedAt) });
  };

  const handleCertify = (initials: string) => {
    if (!listingId || !allComplete || isCertifying) return;
    setCertifyError(null);
    certify({ initials });
  };

  useEffect(() => {
    if (!advisorCalLinkPath) return;
    const namespaces = new Set<string>();
    for (const g of taskGroups) {
      for (const t of g.tasks) {
        if (t.bookingNamespace) namespaces.add(t.bookingNamespace);
      }
    }
    (async () => {
      for (const namespace of namespaces) {
        const cal = await getCalApi({ namespace });
        cal('ui', { hideEventTypeDetails: false, layout: 'month_view', theme: 'dark' });
      }
    })();
  }, [taskGroups, advisorCalLinkPath]);

  const activeGroupComputed = useMemo<TaskGroup | null>(() => {
    for (const g of taskGroups) {
      if (g.tasks.some((task) => !waitingTaskIds.has(task.id) && !isTaskSatisfied(task))) {
        return g.group;
      }
    }
    return null;
  }, [taskGroups, waitingTaskIds, isTaskSatisfied]);

  useEffect(() => {
    setLocalDocumentReadiness(undefined);
    setLocalCompletedTaskIds(new Set());
    setLocalPreApprovalLetterAvailable(undefined);
  }, [dealId, listingId]);

  const expandedGroup =
    manualExpandedGroup === undefined ? activeGroupComputed : manualExpandedGroup;

  useEffect(() => {
    setManualExpandedGroup(undefined);
  }, [activeGroupComputed]);

  useEffect(() => {
    if (
      activeGroupComputed === TaskGroup.DueDiligenceReview &&
      mlsDocumentCheckTask &&
      effectiveCompletedIds.has(mlsDocumentCheckTask.id) &&
      effectiveDocumentReadiness !== undefined
    ) {
      setManualExpandedGroup(TaskGroup.DueDiligenceReview);
    }
  }, [
    activeGroupComputed,
    effectiveCompletedIds,
    effectiveDocumentReadiness,
    mlsDocumentCheckTask,
  ]);

  const toggleGroup = (group: TaskGroup) => {
    setManualExpandedGroup((prev) => {
      const currentExpanded = prev === undefined ? activeGroupComputed : prev;
      return currentExpanded === group ? null : group;
    });
  };

  if (!dealId) {
    return <EmptyState message={emptyMessage ?? 'No property selected'} />;
  }

  if (isLoadingTasks && tasks.length === 0) {
    return <EmptyState message="Loading tasks…" />;
  }

  if (tasks.length === 0) {
    return <EmptyState message="No tasks have been seeded for this deal yet." />;
  }

  const completedAtById = new Map(tasks.map((t) => [t.id, t.completedAt] as const));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {header}
      <ProgressBar completed={completedCount} total={totalCount} />
      {taskGroups.map((group) => {
        const groupActionableTasks = group.tasks.filter((task) => !waitingTaskIds.has(task.id));
        const completed = groupActionableTasks.filter(isTaskSatisfied).length;
        const isExpanded = expandedGroup === group.group;

        return (
          <GroupSection
            key={group.group}
            groupLabel={
              group.group === TaskGroup.DueDiligenceReview
                ? isDocumentReviewActive
                  ? 'Due diligence'
                  : 'Initial property review'
                : group.groupLabel
            }
            completedCount={completed}
            totalCount={groupActionableTasks.length}
            isExpanded={isExpanded}
            onToggleExpand={() => toggleGroup(group.group)}
            cardMode={cardMode}>
            {group.tasks.map((task, index) => {
              const isWaiting = waitingTaskIds.has(task.id);
              const documentCallout =
                task.title === SELLER_DISCLOSURES_REVIEW_TASK_TITLE &&
                sellerDisclosureTaskState !== 'ready' ? (
                  <DocumentTaskCallout
                    label="Seller disclosures"
                    state={sellerDisclosureTaskState}
                    isDocumentReviewActive={isDocumentReviewActive}
                  />
                ) : task.title === HOA_REVIEW_TASK_TITLE && hoaTaskState !== 'ready' ? (
                  <DocumentTaskCallout
                    label="HOA documents"
                    state={hoaTaskState}
                    isDocumentReviewActive={isDocumentReviewActive}
                  />
                ) : undefined;

              return (
                <TaskItem
                  key={task.id}
                  task={task}
                  isLast={index === group.tasks.length - 1}
                  isCompleted={effectiveCompletedIds.has(task.id)}
                  completedAt={completedAtById.get(task.id)}
                  advisorCalLinkPath={advisorCalLinkPath}
                  onToggle={handleToggle}
                  cardMode={cardMode}
                  canComplete
                  isWaiting={isWaiting}
                  isDocumentReviewActive={isDocumentReviewActive}
                  actionContent={
                    task.title === KICKOFF_TASK_TITLE ? (
                      <PreApprovalControl
                        value={effectivePreApprovalLetterAvailable}
                        canEdit={isClient || isAdvisor || isAdmin}
                        isSaving={updateDeal.isPending}
                        errorMessage={
                          updateDeal.error instanceof Error ? updateDeal.error.message : undefined
                        }
                        onChange={handlePreApprovalChange}
                      />
                    ) : task.title === MLS_DOCUMENT_CHECK_TASK_TITLE &&
                      listingId &&
                      activeListing ? (
                      <DocumentReadinessControl
                        value={effectiveDocumentReadiness}
                        canEdit={isAdvisor || isAdmin}
                        isSaving={updateListing.isPending}
                        errorMessage={
                          updateListing.error instanceof Error
                            ? updateListing.error.message
                            : undefined
                        }
                        onChange={handleDocumentReadinessChange}
                      />
                    ) : (
                      documentCallout
                    )
                  }
                />
              );
            })}
          </GroupSection>
        );
      })}
      {futureDueDiligenceTasks.length > 0 && (
        <WaitingTaskSection groupLabel="Due diligence" cardMode={cardMode}>
          {futureDueDiligenceTasks.map((task, index) => {
            const documentCallout =
              task.title === SELLER_DISCLOSURES_REVIEW_TASK_TITLE &&
              sellerDisclosureTaskState !== 'ready' ? (
                <DocumentTaskCallout
                  label="Seller disclosures"
                  state={sellerDisclosureTaskState}
                  isDocumentReviewActive
                />
              ) : task.title === HOA_REVIEW_TASK_TITLE && hoaTaskState !== 'ready' ? (
                <DocumentTaskCallout
                  label="HOA documents"
                  state={hoaTaskState}
                  isDocumentReviewActive
                />
              ) : undefined;

            return (
              <TaskItem
                key={`waiting:${task.id}`}
                task={task}
                isLast={index === futureDueDiligenceTasks.length - 1}
                isCompleted={false}
                advisorCalLinkPath={advisorCalLinkPath}
                onToggle={handleToggle}
                cardMode={cardMode}
                canComplete={false}
                isWaiting
                isDocumentReviewActive
                actionContent={documentCallout}
              />
            );
          })}
        </WaitingTaskSection>
      )}
      {futureContingencyReviewTasks.length > 0 && (
        <WaitingTaskSection groupLabel="Contingency review" cardMode={cardMode}>
          {futureContingencyReviewTasks.map((task, index) => (
            <TaskItem
              key={`waiting:${task.id}`}
              task={task}
              isLast={index === futureContingencyReviewTasks.length - 1}
              isCompleted={false}
              advisorCalLinkPath={advisorCalLinkPath}
              onToggle={handleToggle}
              cardMode={cardMode}
              canComplete={false}
              isWaiting
              isDocumentReviewActive
            />
          ))}
        </WaitingTaskSection>
      )}
      {isDocumentReviewActive ? (
        <FinalCertifyCard
          allComplete={allComplete}
          isCertified={Boolean(activeListing?.certifiedAt)}
          certifiedInitials={activeListing?.certifiedInitials ?? null}
          certifiedByName={certifiedByName}
          certifiedAt={activeListing?.certifiedAt ?? null}
          isCertifying={isCertifying}
          errorMessage={certifyError}
          canCertify={canCertify && Boolean(listingId)}
          onConfirm={handleCertify}
        />
      ) : (
        <Box
          sx={{
            mx: 2,
            mt: '20px',
            mb: 2,
            p: '15px',
            backgroundColor: 'var(--tx-fill)',
            border: 0,
            borderRadius: '10px',
          }}>
          <Typography sx={{ fontSize: 12, color: 'var(--tx-muted)', lineHeight: 1.45 }}>
            Buyer certification becomes available when document due diligence begins.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
